if (!customElements.get("infinite-scroll")) {
  class InfiniteScrollComponent extends HTMLElement {
    constructor() {
      super();
      this.currentPage = 1;
      this.isLoading = false;
      this.hasMorePages = true;
      this.loadedProductsCount = 0;
      this.totalProductsCount = 0;
      this.urlCheckInterval = null;
      this.lastCheckedUrl = null;
      this.options = {
        productsContainer: "#product-grid",
        paginationSelector: ".pagination-wrapper",
        productSelector: ".grid__item",
        autoLoad: true,
        loadMoreText: "Load More Products",
        scrollThreshold: -100,
        storagePrefix: "InfiniteScroll_",
        loadingImageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/9/92/Loading_icon_cropped.gif",
        showProgress: false,
        productCountSelector: ".mobile-facets__count",
      };
    }

    connectedCallback() {
      if (!window.location.pathname.includes("/collections/")) return;
      // Parse attributes
      [
        ["auto-load", (val) => (this.options.autoLoad = val !== "false")],
        ["load-more-text", (val) => (this.options.loadMoreText = val)],
        ["threshold", (val) => (this.options.scrollThreshold = parseInt(val) || 0)],
        ["loading-image", (val) => (this.options.loadingImageUrl = val)],
        ["show-progress", (val) => (this.options.showProgress = val === "true")],
        ["product-count-selector", (val) => (this.options.productCountSelector = val)],
      ].forEach(([attributeName, setOption]) =>
        this.hasAttribute(attributeName) && setOption(this.getAttribute(attributeName))
      );
      // Initialize state and UI (with cache restore on initial load)
      if (!this.initializeState(true)) return;
      this.setupUI();
      // Setup scroll event listener
      if (this.options.autoLoad) {
        this.boundCheckScroll = () => this.productsContainer?.getBoundingClientRect().bottom - this.options.scrollThreshold <= window.innerHeight && this.loadNextPage();
        window.addEventListener("scroll", this.boundCheckScroll);
      }
      // Handle page show event
      this.boundHandlePageShow = (e) => {
        const cachedUrl = this.cache("get", "url");
        if (cachedUrl && cachedUrl !== window.location.href && !e.persisted && !this.hasScrolled) setTimeout(() => {window.scrollTo(0, 0);this.hasScrolled = true;this.cache("set", "hasScrolled", true);}, 20);
      };
      window.addEventListener("pageshow", this.boundHandlePageShow);
      // Start URL monitoring
      this.startUrlMonitoring();
    }

    initializeState(useCache = false) {
      // Re-query DOM elements
      this.productsContainer = document.querySelector(this.options.productsContainer);
      this.pagination = document.querySelector(this.options.paginationSelector);
      if (!this.productsContainer) return false;
      // Get total products count
      const productCountElement = document.querySelector(this.options.productCountSelector);
      // Extract the number from the product count text (e.g., "72 products" -> 72)
      if (productCountElement) {
        const text = productCountElement.textContent.trim();
        const match = text.match(/\d+/);
        this.totalProductsCount = match ? parseInt(match[0]) : 0;
      }
      // Restore cache or initialize fresh
      const cached = useCache && this.cache("get");
      if (cached?.url === window.location.href) {
        this.productsContainer.innerHTML = cached.html;
        this.currentPage = cached.currentPage;
        this.hasMorePages = cached.hasMorePages;
        this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
      } else {
        this.currentPage = 1;
        this.hasMorePages = this.checkHasMorePages(document);
        this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
      }
      // Reset scroll tracking
      this.hasScrolled = this.cache("get", "hasScrolled") || false;
      this.previousUrl = this.cache("get", "previousUrl") || null;
      if (this.previousUrl !== window.location.href) {
        this.previousUrl = window.location.href;
        this.cache("set", "previousUrl", window.location.href);
      }
      return true;
    }

    setupUI() {
      if (!this.pagination) return;
      this.pagination.style.display = "none";
      this.removeLoadingElements();
      if (!this.options.autoLoad && this.hasMorePages) {
        // Create wrapper and all child elements
        this.loadMoreWrapper = this.createElementAfter(this.productsContainer, "div", { class: "infinite-scroll-load-more-wrapper" });
        if (this.options.showProgress) {
          this.productCountText = this.createAndAppend(this.loadMoreWrapper, "p", { class: "infinite-scroll-product-count" });
          this.progressBarBackground = this.createAndAppend(this.loadMoreWrapper, "div", { class: "infinite-scroll-progress-bar-background" });
          this.progressBar = this.createAndAppend(this.progressBarBackground, "div", { class: "infinite-scroll-progress-bar" });
        }
        this.loadMoreBtn = this.createAndAppend(this.loadMoreWrapper, "button", { class: "infinite-scroll-load-more", text: this.options.loadMoreText, onclick: () => this.loadNextPage() });
        this.updateLoadingState(false);
      }
      if (this.options.loadingImageUrl) {
        this.loadingImgContainer = this.createElementAfter(this.loadMoreWrapper || this.productsContainer, "div", {class: "infinite-scroll-loading-image-container"});
        this.loadingImg = this.createAndAppend(this.loadingImgContainer, "img", {class: "infinite-scroll-loading-image", src: this.options.loadingImageUrl, alt: "Loading image", style: "display:none;"});
      }
    }

    createElementAfter(ref, tag, attrs) {
      // Create element after reference and set attributes
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([key, value]) => (key === "text" ? (el.textContent = value) : key === "onclick" ? el.addEventListener("click", value) : el.setAttribute(key, value)));
      ref.insertAdjacentElement("afterend", el);
      return el;
    }

    createAndAppend(parent, tag, attrs) {
      // Create element after parent and append to parent
      const el = this.createElementAfter(parent, tag, attrs);
      parent.appendChild(el);
      return el;
    }

    removeLoadingElements() {
      document.querySelectorAll(".infinite-scroll-load-more-wrapper, .infinite-scroll-load-more, .infinite-scroll-loading-image-container, .infinite-scroll-loading-image").forEach((el) => el.remove());
      // Reset load more wrapper, button and loading image
      this.loadMoreWrapper = this.loadMoreBtn = this.loadingImgContainer = this.loadingImg = null;
    }

    async loadNextPage() {
      if (this.isLoading || !this.hasMorePages) return;
      this.isLoading = true;
      this.updateLoadingState(true);
      this.hasScrolled = false;
      this.cache("set", "hasScrolled", false);
      // Load next page
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("page", this.currentPage + 1);
        // Fetch next page
        const res = await fetch(url.toString(), { credentials: "same-origin" });
        if (!res.ok) return this.handleEndOfResults();
        const doc = new DOMParser().parseFromString(await res.text(), "text/html");
        // Get container and check if it has children
        const container = doc.querySelector(this.options.productsContainer);
        if (!container?.children.length) return this.handleEndOfResults();
        // Clone children and append to products container
        Array.from(container.children).forEach((p) => this.productsContainer.appendChild(p.cloneNode(true)));
        // Update loaded products count and check if there are more pages
        this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
        this.currentPage++;
        this.hasMorePages = this.checkHasMorePages(doc);
        // Check if there are more pages and cache data
        if (!this.hasMorePages) this.handleEndOfResults();
        this.cache("set", {html: this.productsContainer.innerHTML, currentPage: this.currentPage, hasMorePages: this.hasMorePages, url: window.location.href});
      } catch {
        // Handle end of results
        this.handleEndOfResults();
      } finally {
        // Update loading state
        this.isLoading = false;
        this.updateLoadingState(false);
      }
    }

    checkHasMorePages(doc) {
      // Get container and check if it has children and if there is a next page
      const container = doc.querySelector(this.options.productsContainer);
      return container?.children.length > 0 && !!doc.querySelector(`a[href*="page=${this.currentPage + 1}"], .pagination a[rel="next"], .pagination .next:not(.disabled)`);
    }

    updateLoadingState(isLoading) {
      // Update load more button and wrapper and loading image
      if (this.loadMoreBtn) this.loadMoreBtn.disabled = isLoading;
      if (this.loadMoreWrapper) this.loadMoreWrapper.style.display = isLoading ? "none" : "flex";
      if (this.loadingImgContainer) this.loadingImgContainer.style.display = isLoading ? "flex" : "none";
      if (this.loadingImg) this.loadingImg.style.display = isLoading ? "block" : "none";
      // Update product count text and progress bar
      if (this.productCountText && this.totalProductsCount > 0) this.productCountText.textContent = `${this.loadedProductsCount} products loaded out of ${this.totalProductsCount}`;
      if (this.progressBar && this.totalProductsCount > 0) this.progressBar.style.width = `${(this.loadedProductsCount / this.totalProductsCount) * 100}%`;
    }

    handleEndOfResults() {
      this.hasMorePages = false;
      this.removeLoadingElements();
    }

    // Cache controller
    cache(action, key, value) {
      const formatKey = (s) => this.options.storagePrefix + s;
      const serialize = (val) => typeof val === "object" ? JSON.stringify(val) : String(val);
      const deserialize = (val) => {try
        {
          return val ? JSON.parse(val) : null;
        } catch {
          return val;
        }
      };
      try {
        if (action === "get") {
          if (!key) {
            // Get all values from session storage
            const vals = Object.fromEntries(["url", "html", "hasMorePages", "currentPage", "hasScrolled", "previousUrl"].map((k) => [k, deserialize(sessionStorage.getItem(formatKey(k)))]));
            return vals.url && vals.html ? vals : null;
          }
          // Get specific value from session storage
          return deserialize(sessionStorage.getItem(formatKey(key)));
        }
        if (action === "set") {
          // Set specific value or multiple values from session storage
          if (typeof key === "object") Object.entries(key).forEach(([k, val]) => sessionStorage.setItem(formatKey(k), serialize(val)));
          else sessionStorage.setItem(formatKey(key), serialize(value));
        }
        // Clear all values from session storage
        if (action === "clear") Object.keys(sessionStorage).filter((k) => k.startsWith(this.options.storagePrefix)).forEach((k) => sessionStorage.removeItem(k));
      } catch {}
      return null;
    }

    startUrlMonitoring() {
      // Monitor URL and product count changes every 250ms to detect filter changes
      this.lastCheckedUrl = window.location.href;
      this.lastCheckedProductCount = this.totalProductsCount;
      this.lastCheckedProductIds = this.getProductsFingerprint();
      this.urlCheckInterval = setInterval(() => {
        const currentUrl = window.location.href;
        const productCountElement = document.querySelector(this.options.productCountSelector);
        const currentProductCount = productCountElement ? parseInt(productCountElement.textContent.replace("products", "").trim()) : this.totalProductsCount;
        const currentProductIds = this.getProductsFingerprint();
        // Check for URL change or product count decrease
        const urlChanged = currentUrl !== this.lastCheckedUrl;
        const productCountDecreased = currentProductCount < this.lastCheckedProductCount;
        const productsChanged = currentProductIds !== this.lastCheckedProductIds;
        // Only reinitialize if products in the container actually changed
        if ((urlChanged || productCountDecreased) && productsChanged) {
          // Any URL change or product count decrease on a collections page means filters changed
          if (currentUrl.includes("/collections/")) this.handleFilterChange();
          this.lastCheckedUrl = currentUrl;
          this.lastCheckedProductCount = currentProductCount;
          this.lastCheckedProductIds = currentProductIds;
        } else if (urlChanged) {
          // URL changed but products didn't
          this.lastCheckedUrl = currentUrl;
        }
      }, 250);
    }

    getProductsFingerprint() {
      // Returns a simple fingerprint to detect if products changed
      return this.productsContainer?.innerHTML.length || 0;
    }

    handleFilterChange() {
      // Reset loading state and reinitialize
      this.isLoading = false;
      this.initializeState(false);
      this.setupUI();
    }

    disconnectedCallback() {
      if (this.boundCheckScroll) window.removeEventListener("scroll", this.boundCheckScroll);
      if (this.boundHandlePageShow) window.removeEventListener("pageshow", this.boundHandlePageShow);
      if (this.urlCheckInterval) clearInterval(this.urlCheckInterval);
    }
  }
  customElements.define("infinite-scroll", InfiniteScrollComponent);
}